import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import './Profile.css';

const Profile = ({ user }) => {
  const [profile, setProfile] = useState({ first_name: '', last_name: '', phone: '', location: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadProfile = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (data) {
      const parts = (data.full_name || '').trim().split(/\s+/);
      setProfile({
        first_name: user.user_metadata?.first_name || parts[0] || '',
        last_name: user.user_metadata?.last_name || parts.slice(1).join(' ') || '',
        phone: data.phone || '',
        location: data.location || '',
      });
    }
    setLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const first = profile.first_name.trim();
    const last = profile.last_name.trim();
    if (!first || !last) {
      setMessage({ type: 'error', text: 'First and last name are required.' });
      setSaving(false);
      return;
    }
    const fullName = `${first} ${last}`;

    await supabase.auth.updateUser({
      data: { first_name: first, last_name: last, full_name: fullName },
    });

    const { error } = await supabase
      .from('profiles')
      .upsert({
        id: user.id,
        full_name: fullName,
        phone: profile.phone,
        location: profile.location,
        updated_at: new Date().toISOString(),
      });

    if (error) {
      setMessage({ type: 'error', text: error.message });
    } else {
      setMessage({ type: 'success', text: 'Profile updated!' });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-spinner">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">👤</div>
          <h2>Your Profile</h2>
          <p>{user.email}</p>
        </div>

        {message && (
          <div className={`profile-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSave} className="profile-form">
          <div className="form-group">
            <label>First name</label>
            <input
              type="text"
              value={profile.first_name}
              onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
              placeholder="Jane"
              required
            />
          </div>
          <div className="form-group">
            <label>Last name</label>
            <input
              type="text"
              value={profile.last_name}
              onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
              placeholder="Doe"
              required
            />
          </div>

          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              value={profile.location}
              onChange={(e) => setProfile({ ...profile, location: e.target.value })}
              placeholder="City, State"
            />
          </div>

          <button type="submit" className="auth-button" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>

        {/* Change Password */}
        <div className="profile-section">
          <h3>Change Password</h3>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (newPassword.length < 8) {
              setMessage({ type: 'error', text: 'Password must be at least 8 characters.' });
              return;
            }
            if (newPassword !== confirmPassword) {
              setMessage({ type: 'error', text: 'Passwords do not match.' });
              return;
            }
            setChangingPassword(true);
            setMessage(null);
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) {
              setMessage({ type: 'error', text: error.message });
            } else {
              setMessage({ type: 'success', text: 'Password updated!' });
              setNewPassword('');
              setConfirmPassword('');
            }
            setChangingPassword(false);
          }} className="profile-form">
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                minLength={8}
                required
              />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                minLength={8}
                required
              />
            </div>
            <button type="submit" className="auth-button" disabled={changingPassword}>
              {changingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>

        <div className="profile-section">
          <h3>Account Details</h3>
          <div className="detail-row">
            <span className="detail-label">Email</span>
            <span className="detail-value">{user.email}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Member since</span>
            <span className="detail-value">
              {new Date(user.created_at).toLocaleDateString()}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Plan</span>
            <span className="detail-value">Free</span>
          </div>
        </div>
        {/* Delete Account */}
        <div className="profile-section" style={{ borderTop: '1px solid rgba(239,68,68,0.3)', marginTop: 32, paddingTop: 24 }}>
          <h3 style={{ color: '#ef4444' }}>Delete Account</h3>
          <p style={{ fontSize: 14, color: '#999', marginBottom: 12 }}>
            This will permanently delete your account, all your videos, horses, and analysis data. This cannot be undone.
          </p>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}
            >
              Delete My Account
            </button>
          ) : (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: 16 }}>
              <p style={{ fontSize: 14, marginBottom: 12 }}>Type <strong>DELETE</strong> to confirm:</p>
              <input
                type="text"
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                placeholder="DELETE"
                style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #555', background: 'rgba(255,255,255,0.05)', color: '#fff', marginBottom: 12, boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={async () => {
                    if (deleteText !== 'DELETE') return;
                    setDeleting(true);
                    try {
                      // Delete user data from tables
                      await supabase.from('training_labels').delete().eq('reviewer_id', user.id);
                      await supabase.from('user_feedback').delete().eq('user_id', user.id);
                      await supabase.from('body_pattern_ticks').delete().eq('user_id', user.id);
                      await supabase.from('analysis_reports').delete().eq('owner_id', user.id);
                      await supabase.from('videos').delete().eq('owner_id', user.id);
                      await supabase.from('horses').delete().eq('owner_id', user.id);
                      await supabase.from('profiles').delete().eq('id', user.id);
                      // Sign out
                      await supabase.auth.signOut();
                      window.location.href = '/';
                    } catch (err) {
                      setMessage({ type: 'error', text: 'Error deleting account. Please contact support.' });
                      setDeleting(false);
                    }
                  }}
                  disabled={deleteText !== 'DELETE' || deleting}
                  style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: deleteText === 'DELETE' ? '#ef4444' : '#555', color: '#fff', cursor: deleteText === 'DELETE' ? 'pointer' : 'not-allowed', fontWeight: 600 }}
                >
                  {deleting ? 'Deleting...' : 'Permanently Delete'}
                </button>
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeleteText(''); }}
                  style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #555', background: 'transparent', color: '#fff', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
